import { useEffect, useRef, useMemo, useCallback } from 'react'
import Masonry from 'masonry-layout'
import imagesLoaded from 'imagesloaded'
import {
  Download,
  Trash2,
  FileText,
  Loader2,
  Search,
  Filter,
  Sparkles,
  Plus,
} from 'lucide-react'
import { captureError } from '@/lib/sentry.tsx'
import { useNotes } from '@/hooks/useNotes'
import { useAssets } from '@/hooks/useAssets'
import { useBulkExport } from '@/hooks/useBulkExport'
import { useProject } from '@/hooks/useProjects'
import { useNoteStyles } from '@/hooks/useNoteStyles'
import { useImagePipeline } from '@/hooks/useImagePipeline'
import { useProfile } from '@/hooks/useProfile'
import { useR2Upload } from '@/lib/r2'
import { fetchWithAuth } from '@/lib/api'
import { compressBase64 } from '@/lib/compress'
import {
  normalizeAndUploadImage as normalizeAndUploadImageUtil,
  type UploadFunction,
} from '@/lib/referenceImage'
import { normalizeImages, statusText } from '@/lib/imageUtils'
import { cn } from '@/lib/utils'
import type { Note, NoteImage } from '@/types'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from 'sonner'

// Zustand Stores
import { useNoteUIStore } from '@/stores/noteUIStore'
import { useGenerationStore } from '@/stores/generationStore'
import { useQuota } from '@/hooks/useQuota'

// Hooks
import { useTaskRecovery } from '@/hooks/useTaskRecovery'

// 子组件
import {
  NoteCard,
  ShareCard,
  NoteDetailDialog,
  AssetPicker,
  TemplatePicker,
  NewNoteDialog,
} from './xhs'

interface XhsPreviewProps {
  projectId: string
}

type RedrawKey = `${string}-${number}`

export function XhsPreview({ projectId }: XhsPreviewProps) {
  // ========== 数据层 Hooks ==========
  const { assets, markAssetsUsed, releaseAssetsFromNote } = useAssets(projectId)
  const { 
    notes, 
    isLoading, 
    createNote,
    deleteNote, 
    deleteNotes, 
    updateNoteImages, 
    updateNote, 
    updateNoteStatus, 
    batchUpdateStatus, 
    batchUpdateNotesWithImages 
  } = useNotes(projectId, releaseAssetsFromNote)
  const { project } = useProject(projectId)
  const { profile } = useProfile()
  const { templateById, templateMap, slotMap } = useNoteStyles()
  const { dailyLimit, remaining, isNearLimit, isExceeded } = useQuota()
  
  // ========== Refs ==========
  const shareCardRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const masonryInstance = useRef<Masonry | null>(null)

  // ========== 导出逻辑 Hook ==========
  const { handleExport, isExporting, shareCardNote } = useBulkExport({
    shareCardRef,
    notes,
  })

  // ========== Zustand UI Stores ==========
  const {
    selectedIds,
    toggleSelect,
    selectAll,
    clearSelection,
    detailNote,
    openDetail,
    closeDetail,
    updateDetailNote,
    updateDetailImages,
    updateDetailStatus,
    assetPickerOpen,
    assetTarget,
    openAssetPicker,
    closeAssetPicker,
    templatePickerOpen,
    pendingAction,
    pendingNote,
    openTemplatePicker,
    closeTemplatePicker,
  } = useNoteUIStore()

  // ========== 同步详情弹窗数据 ==========
  useEffect(() => {
    if (detailNote) {
      const latestNote = notes.find(n => n.id === detailNote.id)
      if (latestNote && JSON.stringify(latestNote) !== JSON.stringify(detailNote)) {
        updateDetailNote(latestNote)
      }
    }
  }, [notes, detailNote, updateDetailNote])

  const {
    smartLoading,
    setSmartLoading,
    redrawPrompt,
    setRedrawLoading,
    waitingNoteIds,
    setWaitingNoteIds,
    removeWaitingNoteId,
    clearWaitingNoteIds,
    noteProgress,
    updateProgress,
    clearProgress,
    recentlyCompletedIds,
    markAsCompleted,
    clearCompletedMark,
    initCompletedIds,
    saveCompletedIds,
    setActiveBatchIds,
  } = useGenerationStore()

  // ========== 配图生成流水线 ==========
  const { runSmartImages, runBulkImages, flushWrites } = useImagePipeline({
    batchUpdateStatus,
    batchUpdateNotesWithImages,
    updateNoteStatus,
    updateNoteImages,
    markAssetsUsed,
    releaseAssetsFromNote,
    onProgressUpdate: updateProgress,
    onProgressClear: clearProgress,
    onStatusSync: (_noteId, status) => {
      updateDetailStatus(status)
    },
    onImagesSync: (_noteId, images) => {
      updateDetailImages(images)
    },
    onNoteCompleted: (noteId) => {
      markAsCompleted(noteId)
      saveCompletedIds(projectId)
    },
  })

  // ========== 任务恢复 Hook ==========
  useTaskRecovery({
    notes,
    templateId: project?.note_template_id || '', // 尝试获取项目关联模板，或留空让 runBulkImages 处理
    templateById,
    slotMap,
    analyzedAssets: assets.filter((a) => a.analysis_status === 'completed'),
    runBulkImages,
  })

  // ========== 本地 UI 状态 ==========
  const [search, setSearch] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState<'all' | 'draft' | 'planning' | 'generating' | 'completed'>('all')
  const [styleFilter] = React.useState<string>('all')
  const [sortOrder] = React.useState<'desc' | 'asc'>('desc')
  const [newNoteDialogOpen, setNewNoteDialogOpen] = React.useState(false)
  const [isLayoutReady, setIsLayoutReady] = React.useState(false)
  
  // ========== R2 上传 ==========
  const { startUpload: startCollageUpload } = useR2Upload({
    onClientUploadComplete: (res) => {
      console.log('✅ Base64 图片上传完成:', res)
    },
    onUploadError: (error) => {
      console.error('❌ Base64 图片上传失败:', error)
    },
  })

  // ========== 初始化完成标记 ==========
  useEffect(() => {
    initCompletedIds(projectId)
  }, [projectId, initCompletedIds])

  // ========== 心跳检测：超时任务自动恢复 ==========
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now()
      // 如果页面处于后台，放宽阈值到 10 分钟，避免误杀
      const isHidden = document.visibilityState === 'hidden'
      const timeoutThreshold = isHidden ? 600000 : 180000

      Object.entries(noteProgress).forEach(([noteId, progress]) => {
        const timeSinceLastUpdate = now - progress.lastUpdateTime

        if (timeSinceLastUpdate > timeoutThreshold) {
          // 仅在页面可见且任务确实卡死时执行重置，或者在后台时间过长
          console.error(`[心跳检测] 任务超时 (Hidden: ${isHidden}): ${noteId}`)
          clearProgress(noteId)
          setSmartLoading(noteId, false)
          updateNoteStatus(noteId, 'draft').catch(console.error)
          
          // 如果用户能看到，弹出通知
          if (!isHidden) {
            toast.error('笔记配图超时，已自动恢复', { duration: 5000 })
          }
        }
      })
    }, 10000)

    return () => clearInterval(interval)
  }, [noteProgress, clearProgress, setSmartLoading, updateNoteStatus])

  // ========== 页面可见性变化监听：任务恢复提醒 ==========
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // 检查是否有处于处理中但没有进度更新的笔记（可能被浏览器暂停了）
        const processingNotes = notes.filter(n => n.status === 'generating' || n.status === 'planning')
        if (processingNotes.length > 0) {
          const hasIncompleteTasks = processingNotes.some(n => !noteProgress[n.id])
          if (hasIncompleteTasks) {
            toast.info('检测到未完成的任务，正在尝试恢复...', {
              duration: 3000,
            })
          }
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [notes, noteProgress])

  // ========== 可用模版 ==========
  const availableTemplates = useMemo(() => {
    if (!project?.note_style_id) return []
    return templateMap[project.note_style_id] || []
  }, [project?.note_style_id, templateMap])

  // ========== 过滤后的笔记列表 ==========
  const filteredNotes = useMemo(() => {
    const keyword = search.trim().toLowerCase()
    return notes
      .filter((n) => {
        if (statusFilter !== 'all' && n.status !== statusFilter) return false
        if (styleFilter !== 'all' && n.style !== styleFilter) return false
        if (!keyword) return true
        return (
          n.title.toLowerCase().includes(keyword) ||
          n.content.toLowerCase().includes(keyword) ||
          n.hashtags.some((tag) => tag.toLowerCase().includes(keyword))
        )
      })
      .sort((a, b) => (sortOrder === 'desc' ? b.created_at - a.created_at : a.created_at - b.created_at))
  }, [notes, search, statusFilter, styleFilter, sortOrder])

  // ========== 瀑布流 ID 列表 (用于稳定依赖) ==========
  const filteredNoteIds = useMemo(() => filteredNotes.map(n => n.id).join(','), [filteredNotes])

  // ========== Masonry 初始化与更新 ==========
  const layoutTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const performLayout = useCallback(() => {
    if (layoutTimeoutRef.current) clearTimeout(layoutTimeoutRef.current)
    layoutTimeoutRef.current = setTimeout(() => {
      if (masonryInstance.current) {
        masonryInstance.current.reloadItems?.()
        masonryInstance.current.layout?.()
        // 标记布局已准备就绪，允许显示容器
        setIsLayoutReady(true)
      }
    }, 150)
  }, [])

  // 1. 初始化实例 (仅在项目切换或初次挂载时)
  useEffect(() => {
    if (!gridRef.current) return
    
    // 切换项目时先隐藏容器，防止旧项目布局闪烁
    setIsLayoutReady(false)

    const initMasonry = () => {
      if (!gridRef.current) return
      if (masonryInstance.current) {
        masonryInstance.current.destroy?.()
      }

      masonryInstance.current = new Masonry(gridRef.current, {
        itemSelector: '.note-card',
        columnWidth: '.note-card-sizer',
        percentPosition: true,
        gutter: 24,
        transitionDuration: '0.2s',
        initLayout: false,
      })
      masonryInstance.current.layout?.()
      performLayout()
    }

    const timer = setTimeout(initMasonry, 300)
    window.addEventListener('resize', performLayout)

    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', performLayout)
      masonryInstance.current?.destroy?.()
      masonryInstance.current = null
    }
  }, [projectId, performLayout])

  // 2. 监听数据变化和图片加载
  useEffect(() => {
    if (!gridRef.current || filteredNotes.length === 0) return

    const imgLoad = imagesLoaded(gridRef.current)
    imgLoad.on('progress', performLayout)
    imgLoad.on('always', performLayout)
    
    performLayout()

    return () => {
      imgLoad.off('progress', performLayout)
      imgLoad.off('always', performLayout)
    }
  }, [filteredNoteIds, performLayout])

  // ========== 导航逻辑 ==========
  const currentNoteIndex = useMemo(() => {
    if (!detailNote) return -1
    return filteredNotes.findIndex((n) => n.id === detailNote.id)
  }, [detailNote, filteredNotes])

  const handlePrevNote = useCallback(() => {
    if (currentNoteIndex > 0) {
      const nextNote = filteredNotes[currentNoteIndex - 1]
      openDetail(nextNote)
      clearCompletedMark(nextNote.id)
      saveCompletedIds(projectId)
    }
  }, [currentNoteIndex, filteredNotes, openDetail, clearCompletedMark, saveCompletedIds, projectId])

  const handleNextNote = useCallback(() => {
    if (currentNoteIndex < filteredNotes.length - 1) {
      const nextNote = filteredNotes[currentNoteIndex + 1]
      openDetail(nextNote)
      clearCompletedMark(nextNote.id)
      saveCompletedIds(projectId)
    }
  }, [currentNoteIndex, filteredNotes, openDetail, clearCompletedMark, saveCompletedIds, projectId])

  // ========== 键盘快捷键 ==========
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!detailNote) return
      if (e.key === 'ArrowLeft') handlePrevNote()
      if (e.key === 'ArrowRight') handleNextNote()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [detailNote, handlePrevNote, handleNextNote])

  // ========== 组件卸载时保存数据 ==========
  useEffect(() => {
    return () => {
      flushWrites()
    }
  }, [flushWrites])

  // ========== 多选逻辑 ==========
  const toggleSelectAll = useCallback(() => {
    if (selectedIds.length === filteredNotes.length) {
      clearSelection()
    } else {
      selectAll(filteredNotes.map((n) => n.id))
    }
  }, [selectedIds.length, filteredNotes, clearSelection, selectAll])

  // ========== 删除逻辑 ==========
  const handleDeleteNote = useCallback(async (noteId: string) => {
    try {
      await deleteNote(noteId)
      toast.success('笔记已删除')
      if (detailNote?.id === noteId) closeDetail()
    } catch {
      toast.error('删除失败')
    }
  }, [deleteNote, detailNote?.id, closeDetail])

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.length === 0) {
      toast.error('请先选择笔记')
      return
    }
    try {
      await deleteNotes(selectedIds)
      toast.success(`已删除 ${selectedIds.length} 篇笔记`)
      clearSelection()
    } catch (error) {
      console.error(error)
      toast.error('删除失败')
    }
  }, [selectedIds, deleteNotes, clearSelection])

  // ========== 上传函数 ==========
  const uploadFn: UploadFunction = startCollageUpload

  const normalizeAndUploadImage = useCallback(async (urlOrBase64: string, noteId: string, imageIndex: number): Promise<string> => {
    return normalizeAndUploadImageUtil(urlOrBase64, noteId, imageIndex, uploadFn)
  }, [uploadFn])

  // ========== 批量配图 ==========
  const handleBulkGenerateImages = useCallback(() => {
    if (selectedIds.length === 0) {
      toast.error('请先选择笔记')
      return
    }
    if (availableTemplates.length === 0) {
      toast.error('当前项目没有可用的配图模版，请先配置风格和模版')
      return
    }
    openTemplatePicker('bulk')
  }, [selectedIds.length, availableTemplates.length, openTemplatePicker])

  const handleBulkGenerateImagesWithTemplate = useCallback(async (templateId: string, avoidDuplicates: boolean, customInstruction?: string) => {
    if (selectedIds.length === 0) {
      toast.error('请先选择笔记')
      return
    }

    const selectedNotes = notes.filter((n) => selectedIds.includes(n.id))
    const analyzedAssets = assets.filter((a) => a.analysis_status === 'completed')
    
    if (analyzedAssets.length === 0) {
      toast.error('请先上传并完成素材分析后再尝试配图')
      return
    }

    const busyNotes = selectedNotes.filter((n) => smartLoading[n.id])
    if (busyNotes.length > 0) {
      toast.error('部分笔记正在配图中，请稍候再试')
      return
    }

    selectedNotes.forEach(note => {
      setSmartLoading(note.id, true)
    })

    // 设置活跃批次 ID 以便恢复
    setActiveBatchIds(selectedNotes.map(n => n.id))

    // 立即清除选中状态，防止误操作
    clearSelection()

    try {
      toast.success(`开始为 ${selectedNotes.length} 篇笔记生成配图...`, {
        description: '请保持页面开启，后台运行可能导致生成变慢',
        duration: 5000,
      })

      // 包装 setWaitingNoteIds 以兼容 React.Dispatch 类型
      const wrappedSetWaitingNoteIds: React.Dispatch<React.SetStateAction<Set<string>>> = (value) => {
        if (typeof value === 'function') {
          // 内部还是使用 Set 逻辑进行计算，但存储的是数组
          const currentSet = new Set(useGenerationStore.getState().waitingNoteIds)
          const newSet = value(currentSet)
          setWaitingNoteIds(Array.from(newSet))
        } else {
          setWaitingNoteIds(Array.from(value))
        }
      }

      const result = await runBulkImages(
        selectedNotes,
        templateId,
        templateById,
        slotMap,
        analyzedAssets,
        {
          onWaitingIdsUpdate: wrappedSetWaitingNoteIds,
          avoidDuplicates,
          customInstruction,
          isResume: false
        }
      )

      if (result.fail > 0) {
        toast.error(`配图完成`, {
          description: `成功 ${result.success} 篇，失败 ${result.fail} 篇。`,
          duration: 8000,
        })
      } else {
        toast.success(`配图完成`, {
          description: `已为 ${result.success} 篇笔记成功生成配图`,
          duration: 5000,
        })
      }
    } catch (error) {
      console.error('[批量配图] 失败:', error)
      toast.error(error instanceof Error ? error.message : '配图失败，请稍后重试')
    } finally {
      await flushWrites().catch(console.error)
      
      selectedNotes.forEach(note => {
        setSmartLoading(note.id, false)
        clearProgress(note.id)
      })
      clearWaitingNoteIds()
      saveCompletedIds(projectId)
    }
  }, [selectedIds, notes, assets, smartLoading, setSmartLoading, setActiveBatchIds, runBulkImages, templateById, slotMap, setWaitingNoteIds, flushWrites, clearProgress, clearWaitingNoteIds, clearSelection, saveCompletedIds, projectId])

  const handleExportSelected = useCallback(async () => {
    await handleExport(selectedIds)
    clearSelection()
  }, [selectedIds, handleExport, clearSelection])

  // ========== 素材选择 ==========
  const handleSelectAsset = useCallback(async (payload: Partial<NoteImage>) => {
    if (!assetTarget.noteId) return
    const note = notes.find((n) => n.id === assetTarget.noteId)
    if (!note) return
    
    if (smartLoading[note.id] || note.status === 'planning' || note.status === 'generating') {
      toast.error('该笔记正在配图中，请等待完成后再操作')
      closeAssetPicker()
      return
    }

    const nextImages = [...note.images]
    const newImage: NoteImage = {
      index: assetTarget.index,
      type: payload.type || 'single',
      source: payload.source || 'asset',
      url: payload.url,
      status: payload.status || 'completed',
      asset_ids: payload.asset_ids || [],
    }

    if (assetTarget.index >= nextImages.length) {
      nextImages.push(newImage)
    } else {
      nextImages[assetTarget.index] = newImage
    }

    const normalized = normalizeImages(nextImages)
    await updateNoteImages(note.id, normalized)
    
    // 如果有关联素材，标记素材为已使用
    if (newImage.asset_ids && newImage.asset_ids.length > 0) {
      await markAssetsUsed(newImage.asset_ids, note.id)
    }
    
    if (detailNote?.id === note.id) {
      updateDetailImages(normalized)
    }
    
    toast.success('已更新图片')
    closeAssetPicker()
  }, [assetTarget, notes, smartLoading, updateNoteImages, detailNote?.id, updateDetailImages, closeAssetPicker, markAssetsUsed])

  const handleOpenAssetPicker = useCallback((noteId: string, index: number) => {
    if (smartLoading[noteId]) {
      toast.error('该笔记正在配图中，请等待完成后再手动选择素材')
      return
    }
    openAssetPicker(noteId, index)
  }, [smartLoading, openAssetPicker])

  // ========== AI 重绘 ==========
  const handleAiRedraw = useCallback(async (note: Note, index: number, compositeBase64?: string, promptOverride?: string) => {
    const key: RedrawKey = `${note.id}-${index}`
    const prompt = (promptOverride || redrawPrompt[key] || '').trim()
    if (!prompt) {
      toast.error('请先填写重画要求')
      return
    }

    const currentImageUrl = note.images[index]?.url
    if (!compositeBase64 && !currentImageUrl) {
      toast.error('当前图片不存在，无法重绘')
      return
    }

    setRedrawLoading(key, true)

    try {
      // 1. 准备底图：如果是 base64 (涂抹后的)，先进行压缩并上传到 R2
      // 注意：这一步不操作数据库，避免事务中携带巨大的 base64
      let finalInputUrl = currentImageUrl!
      
      if (compositeBase64 && compositeBase64.startsWith('data:')) {
        try {
          // 压缩 base64，避免上传压力
          const compressed = await compressBase64(compositeBase64, 1.0)
          // 上传到 R2 得到 URL
          finalInputUrl = await normalizeAndUploadImage(compressed, note.id, index)
          console.log('[AI重绘] 底图上传成功:', finalInputUrl)
        } catch (error) {
          console.error('[AI重绘] 底图处理或上传失败:', error)
          throw new Error('底图处理失败，请稍后重试')
        }
      }

      // 2. 更新数据库状态为 generating
      // 此时 finalInputUrl 已经是 R2 URL，不再是 base64
      const nextImages = [...note.images]
      if (!nextImages[index]) {
        throw new Error('配图数据异常')
      }
      
      nextImages[index] = {
        ...nextImages[index],
        status: 'generating',
        generation_prompt: prompt,
        source: 'ai_generated',
        // 这一步可选：是否要把上传后的底图 URL 存入 images[index].url？
        // 为了让用户在生成中也能看到底图，可以存入
        url: finalInputUrl, 
      }
      
      const normalized = normalizeImages(nextImages)
      await updateNoteImages(note.id, normalized)
      
      if (detailNote?.id === note.id) {
        updateDetailImages(normalized)
      }

      // 3. 调用 AI 重绘接口
      const requestBody = {
        image_url: finalInputUrl,
        instruction: prompt,
        style: '小红书风格',
        org_id: project?.org_id,
        user_id: profile?.id,
        note_id: note.id,
        image_index: index,
      }

      const resp = await fetchWithAuth('/api/redraw-image', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      })

      let data
      try {
        data = await resp.json()
      } catch (e) {
        if (resp.status === 413) {
          throw new Error('图片数据太大，请尝试缩小涂抹范围')
        }
        throw new Error(`服务器响应异常 (${resp.status})`)
      }

      if (!resp.ok || !data?.success) {
        throw new Error(data?.error || '重绘失败')
      }

      // 4. 处理返回结果：上传 AI 生成的图
      const imageUrl = await normalizeAndUploadImage(data.url || data.base64 || '', note.id, index)
      const updated = [...note.images]
      updated[index] = {
        ...updated[index],
        url: imageUrl,
        status: 'completed',
        generation_prompt: prompt,
        source: 'ai_generated',
      }
      const finalNormalized = normalizeImages(updated)
      await updateNoteImages(note.id, finalNormalized)
      if (detailNote?.id === note.id) {
        updateDetailImages(finalNormalized)
      }
      toast.success('AI 重绘完成')
    } catch (error) {
      captureError(error, { feature: 'redraw', action: 'ai-redraw', noteId: note.id, imageIndex: index })
      console.error(error)
      
      // 失败处理：确保回退到 failed 状态，且不携带任何 base64
      try {
        const failed = [...note.images]
        if (failed[index]) {
          failed[index] = {
            ...failed[index],
            status: 'failed',
            generation_prompt: prompt,
            // 如果 url 是 base64 (虽然我们已经尽量避免了)，强制清空或保持原样
            url: failed[index].url?.startsWith('data:') ? '' : failed[index].url
          }
          const failedNormalized = normalizeImages(failed)
          await updateNoteImages(note.id, failedNormalized)
          if (detailNote?.id === note.id) {
            updateDetailImages(failedNormalized)
          }
        }
      } catch (dbError) {
        console.error('更新失败状态到数据库时也失败了:', dbError)
      }
      
      toast.error(error instanceof Error ? error.message : '重绘失败')
    } finally {
      setRedrawLoading(key, false)
    }
  }, [redrawPrompt, updateNoteImages, detailNote?.id, updateDetailImages, setRedrawLoading, normalizeAndUploadImage, project, profile])

  // ========== 文本编辑 ==========
  const handleUpdateText = useCallback(async (noteId: string, field: 'title' | 'content', value: string) => {
    await updateNote(noteId, { [field]: value })
  }, [updateNote])

  const handleUpdateHashtags = useCallback(async (noteId: string, hashtags: string[]) => {
    await updateNote(noteId, { hashtags })
  }, [updateNote])

  // ========== 中止生成 ==========
  const handleAbort = useCallback(async (noteId: string) => {
    try {
      // 1. 同步数据库状态和图片 (清除生成中的图片状态)
      const note = notes.find(n => n.id === noteId)
      if (note) {
        const cleanedImages = note.images.map(img => 
          (img.status === 'generating' || img.status === 'failed') 
            ? { ...img, status: 'pending' as const } 
            : img
        )
        // 使用原子操作批量更新状态和图片
        await batchUpdateNotesWithImages([{
          noteId,
          status: 'draft',
          images: cleanedImages
        }])
        
        // 如果详情页正开着这个笔记，同步更新详情页数据
        if (detailNote?.id === noteId) {
          updateDetailStatus('draft')
          updateDetailImages(cleanedImages)
        }
      } else {
        // 兜底：仅更新状态
        await updateNoteStatus(noteId, 'draft')
        if (detailNote?.id === noteId) {
          updateDetailStatus('draft')
        }
      }
      
      // 2. 释放关联素材，确保素材可以重新被分配
      await releaseAssetsFromNote(noteId).catch(err => 
        console.warn('[中止生成] 释放素材失败:', err)
      )

      // 3. 清除 Store 中的加载状态、进度和等待列表
      setSmartLoading(noteId, false)
      clearProgress(noteId)
      removeWaitingNoteId(noteId)
      
      toast.success('已中止生成任务')
    } catch (error) {
      console.error('[中止生成] 失败:', error)
      toast.error('中止失败，请重试')
    }
  }, [
    notes, 
    updateNoteStatus, 
    batchUpdateNotesWithImages, 
    releaseAssetsFromNote, 
    clearProgress, 
    setSmartLoading, 
    removeWaitingNoteId, 
    detailNote?.id, 
    updateDetailStatus, 
    updateDetailImages
  ])

  // ========== 智能配图 ==========
  const handleSmartImages = useCallback((note: Note) => {
    if (smartLoading[note.id]) {
      toast.error('该笔记正在配图中，请稍候')
      return
    }

    const analyzedAssets = assets.filter((a) => a.analysis_status === 'completed')
    if (analyzedAssets.length === 0) {
      toast.error('请先上传并完成素材分析后再尝试智能配图')
      return
    }

    if (availableTemplates.length === 0) {
      toast.error('当前项目没有可用的配图模版，请先配置风格和模版')
      return
    }

    openTemplatePicker('smart', note)
  }, [smartLoading, assets, availableTemplates.length, openTemplatePicker])

  const handleSmartImagesWithTemplate = useCallback(async (note: Note, templateId: string, avoidDuplicates: boolean, customInstruction?: string) => {
    if (smartLoading[note.id]) {
      toast.error('该笔记正在配图中，请稍候')
      return
    }

    const analyzedAssets = assets.filter((a) => a.analysis_status === 'completed')
    
    if (analyzedAssets.length === 0) {
      toast.error('请先上传并完成素材分析后再尝试智能配图')
      return
    }

    setSmartLoading(note.id, true)

    try {
      toast.info('正在生成智能配图...', {
        description: '请保持页面开启，后台运行可能导致生成变慢',
        duration: 3000,
      })
      await runSmartImages(note, templateId, templateById, slotMap, analyzedAssets, avoidDuplicates, customInstruction)
      saveCompletedIds(projectId)
    } catch (error) {
      console.error(error)
      await updateNoteStatus(note.id, 'draft')
      updateDetailStatus('draft')
      clearProgress(note.id)
      toast.error(error instanceof Error ? error.message : '智能配图失败，请稍后重试')
    } finally {
      setSmartLoading(note.id, false)
    }
  }, [smartLoading, assets, setSmartLoading, runSmartImages, templateById, slotMap, updateNoteStatus, updateDetailStatus, clearProgress, saveCompletedIds, projectId])

  // ========== 模版选择确认 ==========
  const handleTemplateConfirm = useCallback((templateId: string, avoidDuplicates: boolean, customInstruction: string) => {
    if (pendingAction === 'bulk') {
      handleBulkGenerateImagesWithTemplate(templateId, avoidDuplicates, customInstruction)
    } else if (pendingAction === 'smart' && pendingNote) {
      handleSmartImagesWithTemplate(pendingNote, templateId, avoidDuplicates, customInstruction)
    }
    closeTemplatePicker()
  }, [pendingAction, pendingNote, handleBulkGenerateImagesWithTemplate, handleSmartImagesWithTemplate, closeTemplatePicker])

  // ========== 手动创建笔记 ==========
  const handleCreateNote = async (data: { title: string; content: string; hashtags: string[] }) => {
    await createNote({
      ...data,
      style: project?.note_style_id || 'default',
      images: [],
    })
  }

  // ========== 渲染 ==========
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!notes.length) {
    return (
      <>
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                笔记仓库
              </div>
              <Button size="sm" onClick={() => setNewNoteDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-1" /> 新建笔记
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="py-10 text-center text-muted-foreground">
            <p>暂无笔记</p>
            <p className="text-sm mt-2">点击上方按钮手动创建，或在「文案生成」中 AI 创建</p>
          </CardContent>
        </Card>
        <NewNoteDialog
          open={newNoteDialogOpen}
          onOpenChange={setNewNoteDialogOpen}
          onSubmit={handleCreateNote}
        />
      </>
    )
  }

  return (
    <div className="space-y-6">
      {/* 顶部操作栏 */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between bg-white p-4 rounded-xl border shadow-sm">
        <div className="flex items-center gap-2 flex-1 w-full md:w-auto">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索笔记..."
              className="pl-9 h-9"
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 w-9 p-0">
                <Filter className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuLabel>状态筛选</DropdownMenuLabel>
              {['all', 'completed', 'draft', 'planning', 'generating'].map((key) => (
                <DropdownMenuCheckboxItem
                  key={key}
                  checked={statusFilter === key}
                  onCheckedChange={() => setStatusFilter(key as typeof statusFilter)}
                >
                  {statusText(key as typeof statusFilter)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto justify-between md:justify-end">
          <div className="flex items-center gap-2 text-sm">
            <Checkbox
              id="select-all"
              checked={selectedIds.length === filteredNotes.length && filteredNotes.length > 0}
              onCheckedChange={toggleSelectAll}
            />
            <Label htmlFor="select-all" className="cursor-pointer text-muted-foreground">全选</Label>
            {selectedIds.length > 0 && (
              <span className="text-muted-foreground">({selectedIds.length})</span>
            )}
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setNewNoteDialogOpen(true)}
            >
              <Plus className="w-4 h-4 mr-1" /> 新建
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleBulkGenerateImages} 
              disabled={selectedIds.length === 0 || isExceeded}
              title={isExceeded ? '今日AI生图已用完' : ''}
            >
              <Sparkles className="w-4 h-4 mr-1" /> 配图
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportSelected} disabled={selectedIds.length === 0 || isExporting}>
              {isExporting ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-1" />
              )}
              导出
            </Button>
            <Button variant="outline" size="sm" onClick={handleBulkDelete} disabled={selectedIds.length === 0} className="text-destructive hover:text-destructive">
              <Trash2 className="w-4 h-4 mr-1" /> 删除
            </Button>
          </div>
        </div>
      </div>

      {/* 瀑布流展示 */}
      <div 
        ref={gridRef} 
        className={cn(
          "w-full relative min-h-[500px] transition-opacity duration-300",
          isLayoutReady ? "opacity-100" : "opacity-0"
        )}
      >
        <div className="note-card-sizer w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] xl:w-[calc(25%-18px)]" />
        
        {filteredNotes.map((note) => (
          <div key={note.id} className="note-card w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] xl:w-[calc(25%-18px)] mb-6">
            <NoteCard
              note={note}
              selected={selectedIds.includes(note.id)}
              onSelect={() => toggleSelect(note.id)}
              onClick={() => {
                openDetail(note)
                clearCompletedMark(note.id)
                saveCompletedIds(projectId)
              }}
              onAbort={handleAbort}
              isWaiting={waitingNoteIds.includes(note.id)}
              isRecentlyCompleted={recentlyCompletedIds.includes(note.id)}
              progress={noteProgress[note.id]}
            />
          </div>
        ))}
      </div>

      {/* 详情弹窗 - 直接从 Store 订阅重绘状态 */}
      <NoteDetailDialog
        note={detailNote}
        onClose={closeDetail}
        currentNoteIndex={currentNoteIndex}
        totalNotes={filteredNotes.length}
        onPrevNote={handlePrevNote}
        onNextNote={handleNextNote}
        onSmartImages={handleSmartImages}
        onOpenAssetPicker={handleOpenAssetPicker}
        onDeleteNote={handleDeleteNote}
        onUpdateText={handleUpdateText}
        onUpdateHashtags={handleUpdateHashtags}
        onAiRedraw={handleAiRedraw}
        onUpdateImages={(noteId, images) => {
          const normalized = normalizeImages(images)
          updateNoteImages(noteId, normalized)
          updateDetailImages(normalized)
        }}
        onAbort={handleAbort}
      />
      
      {/* 隐藏的分享卡片 */}
      <div className="absolute left-[-9999px] top-[-9999px] opacity-0 pointer-events-none">
        <div ref={shareCardRef}>
          {shareCardNote && <ShareCard note={shareCardNote} />}
        </div>
      </div>

      {/* 素材库选择器 */}
      <AssetPicker
        open={assetPickerOpen}
        onOpenChange={(open) => !open && closeAssetPicker()}
        assets={assets}
        projectId={projectId}
        onSelect={handleSelectAsset}
      />

      {/* 模版选择弹窗 */}
      <TemplatePicker
        open={templatePickerOpen}
        onOpenChange={(open) => !open && closeTemplatePicker()}
        templates={availableTemplates}
        assets={assets}
        noteCount={pendingAction === 'bulk' ? selectedIds.length : 1}
        onConfirm={handleTemplateConfirm}
        quota={{ remaining, dailyLimit, isNearLimit, isExceeded }}
      />

      <NewNoteDialog
        open={newNoteDialogOpen}
        onOpenChange={setNewNoteDialogOpen}
        onSubmit={handleCreateNote}
      />
    </div>
  )
}

// 需要导入 React
import React from 'react'
